"use client";

import { useEffect, useRef } from "react";
import { useAuth } from "@clerk/nextjs";
import { usePathname } from "next/navigation";
import { initAnalytics, setAnalyticsUserId, trackEvent } from "@/lib/client/analytics";
import { getCookie } from "@/lib/client/cookies";

const AnalyticsInit = () => {
  const { userId } = useAuth();
  const pathname = usePathname();
  const prevPathname = useRef<string | null>(null);
  const firstInteractionFired = useRef(false);
  const pageLoadTime = useRef<number>(0);

  useEffect(() => {
    if (getCookie("cookieConsent") !== "true") return;
    initAnalytics(userId);
  }, [userId]);

  useEffect(() => {
    if (getCookie("cookieConsent") !== "true") return;
    if (pathname === prevPathname.current) return;

    prevPathname.current = pathname;
    trackEvent("page_viewed", "navigation", {
      path: pathname,
      referrer: document.referrer || undefined,
      device_type:
        window.innerWidth < 768 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop",
    });
  }, [pathname]);

  useEffect(() => {
    setAnalyticsUserId(userId || null);
  }, [userId]);

  useEffect(() => {
    if (getCookie("cookieConsent") !== "true") return;

    pageLoadTime.current = Date.now();
    firstInteractionFired.current = false;

    const handleFirstInteraction = () => {
      if (firstInteractionFired.current) return;
      firstInteractionFired.current = true;

      try {
        const timeMs = Date.now() - pageLoadTime.current;
        trackEvent("time_to_first_interaction", "session", {
          time_ms: timeMs,
          path: window.location.pathname,
          device_type:
            window.innerWidth < 768 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop",
        });
      } catch {
        // Analytics must never affect user interaction
      }

      window.removeEventListener("click", handleFirstInteraction);
      window.removeEventListener("scroll", handleFirstInteraction);
      window.removeEventListener("keydown", handleFirstInteraction);
    };

    window.addEventListener("click", handleFirstInteraction, { once: true, passive: true });
    window.addEventListener("scroll", handleFirstInteraction, { once: true, passive: true });
    window.addEventListener("keydown", handleFirstInteraction, { once: true, passive: true });

    return () => {
      window.removeEventListener("click", handleFirstInteraction);
      window.removeEventListener("scroll", handleFirstInteraction);
      window.removeEventListener("keydown", handleFirstInteraction);
    };
  }, []);

  return null;
};

export default AnalyticsInit;
